import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing, Clock, Moon, ArrowRight, Repeat, Target, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

const WD = {
  en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  hi: ["सोमवार", "मंगलवार", "बुधवार", "गुरुवार", "शुक्रवार", "शनिवार", "रविवार"],
};

function timeLabel(r, language) {
  if (!r.next_at) return "";
  const locale = language === "hi" ? "hi-IN" : "en-IN";
  return new Date(r.next_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit", timeZone: r.timezone });
}

// A calm "here's what's due today" summary at the top of My EVERMIN.
// Shows reminders due now + anything scheduled later today (browser-local day).
export function ReminderDigest() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    api.get("/reminders").then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => {
    load();
    const onFired = () => load();
    window.addEventListener("evermin:reminder-fired", onFired);
    window.addEventListener("focus", onFired);
    return () => {
      window.removeEventListener("evermin:reminder-fired", onFired);
      window.removeEventListener("focus", onFired);
    };
  }, [load]);

  const todayStr = new Date().toDateString();
  const dueToday = items
    .filter((r) => r.status === "active" && r.next_at && (r.due || new Date(r.next_at).toDateString() === todayStr))
    .sort((a, b) => new Date(a.next_at) - new Date(b.next_at));

  if (!dueToday.length) return null;

  const snooze = async (r, mode) => {
    try {
      await api.post(`/reminders/${r.reminder_id}/snooze`, { mode });
      toast.success(t("rem_snoozed"));
      load();
    } catch { /* ignore */ }
  };
  const complete = async (r) => {
    try { await api.post(`/reminders/${r.reminder_id}/complete`); load(); } catch { /* ignore */ }
  };

  return (
    <div className="mt-6 rounded-2xl border border-primary/20 bg-primary/5 p-5" data-testid="reminder-digest">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-display text-base font-bold text-slate-900">
          <BellRing className="h-4.5 w-4.5 h-5 w-5 text-primary" /> {t("digest_title")}
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">{dueToday.length}</span>
        </h2>
        <button onClick={() => navigate("/reminders")} data-testid="digest-viewall" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
          {t("digest_manage")} <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {dueToday.slice(0, 5).map((r) => (
          <div key={r.reminder_id} data-testid={`digest-item-${r.reminder_id}`}
            className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${r.due ? "bg-rose-500" : "bg-primary"}`} />
                <p className="truncate text-sm font-semibold text-slate-900">{r.title}</p>
                {r.recurrence !== "none" && <Repeat className="h-3.5 w-3.5 flex-shrink-0 text-primary" />}
                {r.goal_id && <Target className="h-3.5 w-3.5 flex-shrink-0 text-violet-500" />}
              </div>
              <p className="mt-0.5 pl-4 text-xs text-slate-500">
                {r.due ? <span className="font-medium text-rose-600">{t("digest_due_now")}</span> : timeLabel(r, language)}
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5 pl-4 sm:pl-0">
              {r.due && (
                <>
                  <DigestBtn testid={`digest-snooze-1h-${r.reminder_id}`} onClick={() => snooze(r, "1h")} accent><Clock className="h-3.5 w-3.5" /> {t("rem_snooze_1h")}</DigestBtn>
                  <DigestBtn testid={`digest-snooze-evening-${r.reminder_id}`} onClick={() => snooze(r, "evening")} accent><Moon className="h-3.5 w-3.5" /> {t("rem_snooze_evening")}</DigestBtn>
                </>
              )}
              <DigestBtn testid={`digest-complete-${r.reminder_id}`} onClick={() => complete(r)}><CheckCircle2 className="h-3.5 w-3.5" /> {t("rem_complete")}</DigestBtn>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DigestBtn({ children, onClick, testid, accent }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors min-h-[32px] ${accent ? "border-primary/30 bg-primary/5 text-primary hover:bg-primary/10" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}
